<script lang="ts">
  import { CATEGORY_META, TIMINGS, categoryLabel, categoryShort, type AnswerValue, type Category, type PlayerView } from '@banger/shared';
  import { decadeLabel } from '../lib/answerInput';
  import { resultStep } from '../lib/resultStep';
  import CardGrid from './CardGrid.svelte';
  import MarkPrompt from './MarkPrompt.svelte';
  import StealPicker from './StealPicker.svelte';

  let { view, secs, sittingOut = false, outline = [] }: {
    view: PlayerView; secs: number; sittingOut?: boolean;
    /** Album Track 2: the Remix pattern's cells, outlined on the card. */
    outline?: readonly number[];
  } = $props();

  const me = $derived(view.me);
  const r = $derived(view.reveal);
  const cat = $derived(view.category as Category);
  const cutoff = $derived(view.cutoffYear ?? 2000);
  const answered = $derived(me.answer !== null);
  const correct = $derived(me.answer?.correct === true);
  const album = $derived(view.mode === 'album');

  function correctAnswer(c: Category): string {
    if (!r) return '';
    switch (c) {
      case 'title': return r.title;
      case 'artist': return r.artist;
      case 'decade': return `${decadeLabel(Math.floor(r.year / 10) * 10)} (${r.year})`;
      case 'before2000': return `${r.year < cutoff ? 'BEFORE' : 'AFTER'} ${cutoff} (${r.year})`;
      case 'year4': return String(r.year);
    }
  }

  function said(v: AnswerValue): string {
    if (typeof v === 'boolean') return `${v ? 'BEFORE' : 'AFTER'} ${cutoff}`;
    if (cat === 'decade' && typeof v === 'number') return decadeLabel(v);
    return String(v);
  }

  // The steal offer vanishes from the view once used; keep showing the confirmation.
  let stealEarned = $state(false);
  $effect(() => { if (me.canSteal !== null) stealEarned = true; });

  const marking = $derived(me.card !== null && (me.canMark.length > 0 || me.markedThisRound !== null));
  // Mark first (one tap), then the steal: the tall steal picker would push the mark off-screen.
  const step = $derived(resultStep({ canMark: me.canMark, markedThisRound: me.markedThisRound, stealEarned, secsLeft: secs, stealAtSecs: TIMINGS.markMs / 1000 }));
  const autoNote = $derived(step === 'steal' && me.canMark.length > 0 && me.markedThisRound === null && me.canSteal !== null);
</script>

<section class="result" style="--c:{CATEGORY_META[cat].color}">
  <div class="verdict" class:right={correct}>
    <span class="icon">{sittingOut ? '🍿' : correct ? '✅' : '❌'}</span>
    <div class="txt">
      <p class="answer">{correctAnswer(cat)}</p>
      {#if cat !== 'title' && cat !== 'artist' && r}<p class="song">{r.title} — {r.artist}</p>{/if}
      <p class="mine">
        {#if album && correct}<b class="pt">+1 ⭐</b> {/if}{#if sittingOut}Tiebreak round — you sat this one out{:else if !answered}No answer{:else if me.answer?.exact}🎯 Exact! You said {said(me.answer.value)}{:else}You said {said(me.answer!.value)}{/if}
      </p>
    </div>
  </div>

  {#if !view.paused}
    {#if autoNote}
      <!-- Steal window opened by time: the tall mark prompt would hide it; the server auto-marks. -->
    {:else if marking && me.card}
      <MarkPrompt
        category={cat}
        card={me.card}
        marked={me.marked}
        canMark={me.canMark}
        markedThisRound={me.markedThisRound}
        round={view.roundNumber}
        {secs}
        {cutoff}
        track={album ? view.track : null}
        {outline}
      />
      {#if step === 'mark' && stealEarned}<p class="then">Then you get to steal! 😈</p>{/if}
    {:else if correct && me.card}
      <p class="note">No {categoryShort(cat, cutoff)} squares left — it still counts!</p>
    {/if}
    {#if step === 'steal'}
      {#if autoNote}<p class="then">We'll auto-mark your best square — steal now! 😈</p>{/if}
      <StealPicker targets={me.canSteal} round={view.roundNumber} {secs} />
    {/if}
    {#if !marking && me.card}
      <CardGrid card={me.card} marked={me.marked} {cutoff} {outline} />
    {/if}
  {/if}
</section>

<style>
  .result { display: grid; gap: 16px; width: 100%; }
  .verdict {
    display: flex; align-items: center; gap: 14px; padding: 12px 16px; border-radius: 16px;
    background: rgba(255, 255, 255, 0.05); border: 2px solid rgba(255, 255, 255, 0.15);
  }
  .verdict.right { border-color: #69db7c; box-shadow: 0 0 18px rgba(105, 219, 124, 0.4); }
  .icon { font-size: 2.6rem; line-height: 1; }
  .txt { min-width: 0; display: grid; gap: 2px; }
  .answer { margin: 0; font-size: 1.5rem; font-weight: 900; color: var(--c); text-shadow: 0 0 10px color-mix(in srgb, var(--c) 60%, transparent); word-break: break-word; }
  .song { margin: 0; font-size: 0.95rem; opacity: 0.8; }
  .mine { margin: 0; font-size: 1rem; opacity: 0.85; word-break: break-word; }
  .pt { color: var(--cyan); text-shadow: 0 0 8px var(--cyan); }
  .then { margin: -8px 0 0; text-align: center; font-size: 0.95rem; font-weight: 700; opacity: 0.85; }
  .note { margin: 0; text-align: center; font-size: 1.1rem; font-weight: 700; opacity: 0.85; }
</style>
